import type { QueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'

import { api, refreshSession, tenantStore, tokenStore } from '@/lib/api/client'
import type { components } from '@/lib/api/schema'
import { tokenExpiresAt } from '@/lib/api/token'

import { authQueries } from './queries'
import { can, type Capability } from './roles'

export type Membership = components['schemas']['MembershipSummary']

/**
 * Why a session ended, which is the whole of what the sign-in screen has to say about it.
 *
 * `user` asked to leave; `expired` is a session the API stopped accepting; `idle` is the
 * dealership's own timeout (ADR 0009), and it is separate because "you were signed out for
 * inactivity" is the difference between a rule and a fault.
 */
export type SignOutReason = 'user' | 'expired' | 'idle'

export function hasValidToken(now = Date.now()): boolean {
  const token = tokenStore.get()
  if (!token) return false
  const expiresAt = tokenExpiresAt(token)
  return expiresAt === null || expiresAt > now
}

/**
 * Is there a session to go on with? Asked once, before the guard sends anyone to sign-in.
 *
 * The access token is the short half and is expected to be stale — after fifteen minutes in
 * another tab, after a night with the laptop shut, on every cold start of the installed PWA.
 * The long half is the httpOnly cookie, and the only way to find out whether it is still good is
 * to spend it. So a missing or expired token is a question for the API, not a sign-out.
 */
export async function hasSession(): Promise<boolean> {
  return hasValidToken() || refreshSession()
}

/** Keep the remembered dealership if the user is still a member of it, else the first one. */
export function resolveTenantId(
  memberships: readonly Membership[],
  preferred: string | null,
): string | null {
  if (preferred !== null && memberships.some((m) => m.tenant_id === preferred)) return preferred
  return memberships[0]?.tenant_id ?? null
}

/** Post-login destinations must be in-app paths: no open redirects, no bouncing back to auth. */
export function safeRedirect(target: string | undefined): string {
  if (
    !target ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    target.startsWith('/login') ||
    target.startsWith('/sign-up')
  ) {
    return '/'
  }
  return target
}

// Why the token disappeared decides what the sign-in page says and whether it offers to
// return the user to where they were. Any new token resets it.
let signOutReason: SignOutReason = 'expired'
tokenStore.subscribe(() => {
  if (tokenStore.get() !== null) signOutReason = 'expired'
})

export function lastSignOutReason(): SignOutReason {
  return signOutReason
}

export function signOut(queryClient: QueryClient, reason: SignOutReason = 'user'): void {
  signOutReason = reason
  // Not awaited, and its failure is not an error the user should see: the session is over on
  // this device the moment the token goes, and the server's copy expires on its own clock if
  // the request never lands. Sent before the token is dropped so it carries one.
  if (reason === 'user') void api.POST('/api/v1/auth/logout').catch(() => undefined)
  tokenStore.set(null)
  tenantStore.set(null)
  queryClient.clear()
}

/** Switch dealership in place: drop every cached row of the old one, keep the profile. */
export function switchTenant(queryClient: QueryClient, tenantId: string): void {
  if (tenantStore.get() === tenantId) return
  tenantStore.set(tenantId)
  void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
}

export function useAccessToken(): string | null {
  return useSyncExternalStore(tokenStore.subscribe, tokenStore.get)
}

export function useTenantId(): string | null {
  return useSyncExternalStore(tenantStore.subscribe, tenantStore.get)
}

/**
 * The capability check for a route's `beforeLoad`, once `_app` has put the profile in the cache
 * and selected a dealership: the same lookup `useCan` makes, without a component to make it in.
 */
export function cachedCan(queryClient: QueryClient, capability: Capability): boolean {
  const me = queryClient.getQueryData(authQueries.me().queryKey)
  const membership = me?.memberships.find((m) => m.tenant_id === tenantStore.get())
  return can(membership?.role, capability)
}
